import { graphHooks } from '@tunasong/graph-lib/react'
import { useCurrentUser } from '@tunasong/redux'
import type { Persisted, Poll, PollResponse } from '@tunasong/schemas'
import { isDatePoll, isManyOfManyPoll, isOneOfManyPoll } from '@tunasong/schemas'
import { Typography, UserAvatar } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { ManyOfManyPollView } from './many-of-many/view.js'
import { OneOfManyPollView } from './one-of-many/view.js'
import { buildMemberNames } from './member-names.js'
import { ResponsesMatrix } from './responses-matrix.js'
import styles from './responses.module.css'

export interface ResponsesProps {
  poll: Persisted<Poll>
}

const Responses: FC<ResponsesProps> = props => {
  const { poll } = props

  const { entities: responses } = graphHooks.useEntityChildren<PollResponse>({
    parentId: poll.id,
    filter: 'pollresponse',
  })
  const { members } = graphHooks.useMembers(poll)
  const { userId: currentUserId } = useCurrentUser()

  const userIds = responses ? responses.map(r => r.userId) : []

  /**
   * A rehearsal poll reads as a respondent × slot matrix. It also loads once: the
   * per-respondent rendering below re-runs the same children query for every respondent.
   */
  if (isDatePoll(poll)) {
    return (
      <ResponsesMatrix
        poll={poll}
        responses={responses ?? []}
        memberIds={members.map(member => member.userId)}
        memberNames={buildMemberNames(members, currentUserId)}
      />
    )
  }

  return (
    <>
      {userIds.length === 0 ? (
        <Typography className={styles.noResponses} color="secondary">
          No responses yet
        </Typography>
      ) : null}
      {userIds.map(userId => (
        <div key={userId} className={styles.responseRow}>
          <UserAvatar userId={userId} className={styles.avatar} />
          {isOneOfManyPoll(poll) ? <OneOfManyPollView userId={userId} poll={poll} /> : null}
          {isManyOfManyPoll(poll) ? <ManyOfManyPollView userId={userId} poll={poll} /> : null}
        </div>
      ))}
    </>
  )
}

export default Responses
